import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Plus, Truck, CheckCircle2, Pause } from 'lucide-react';
import api from '../../../services/api';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import Select from '../../../components/shared/Select/Select';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import { createVehicle, updateVehicle } from '../../../services/vehicles';
import { listUsers } from '../../../services/accounts';
import { useAuth } from '../../../context/AuthContext';
import type { Vehicle, User } from '../../../types/api';

// Common PSV seat capacities. Any existing non-listed value is preserved when editing.
const CAPACITY_PRESETS = ['11', '14', '15', '16', '24', '33'];

interface CreateForm {
  registration_number: string;
  capacity: string;
  vehicle_type: string;
  owner: string;
}

const empty: CreateForm = { registration_number: '', capacity: '', vehicle_type: 'Matatu', owner: '' };

const Vehicles: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';   // only admins may create/edit vehicles
  const { data: vehicles, loading, refetch } = usePaginatedResource<Vehicle>('/vehicles/');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form, setForm] = useState<CreateForm>(empty);

  // Partner users (vehicle owners) for the searchable owner picker.
  const [owners, setOwners] = useState<User[]>([]);
  useEffect(() => {
    listUsers({ role: 'partner', page_size: 1000 }).then(setOwners).catch(() => setOwners([]));
  }, []);
  const ownerOptions = useMemo(
    () => [
      { value: '', label: 'Unassigned (company-owned)' },
      ...owners.map((u) => ({
        value: String(u.id),
        label: [`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(), u.phone_number]
          .filter(Boolean).join(' · ') || `User #${u.id}`,
      })),
    ],
    [owners],
  );

  const save = useResourceMutation(
    async (state: CreateForm) => {
      const payload = {
        registration_number: state.registration_number,
        capacity: parseInt(state.capacity, 10),
        vehicle_type: state.vehicle_type,
        owner: state.owner ? parseInt(state.owner, 10) : null,
      };
      return editing ? updateVehicle(editing.id, payload) : createVehicle(payload);
    },
    { successMessage: 'Vehicle saved' }
  );

  const toggleActive = useResourceMutation(
    async (v: Vehicle) => {
      await api.patch(`/vehicles/${v.id}/`, { is_active: !v.is_active });
    },
    { successMessage: 'Vehicle status updated' }
  );

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (v: Vehicle) => {
    setEditing(v);
    setForm({
      registration_number: v.registration_number,
      capacity: v.capacity.toString(),
      vehicle_type: v.vehicle_type,
      owner: v.owner?.toString() ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      await save.run(form);
      setShowForm(false);
      refetch();
    } catch { /* toast */ }
  };

  const handleToggle = async (v: Vehicle) => {
    try {
      await toggleActive.run(v);
      refetch();
    } catch { /* toast */ }
  };

  const columns: Column<Vehicle>[] = [
    { key: 'registration_number', header: 'Reg #', sortable: true },
    { key: 'vehicle_type',        header: 'Type' },
    { key: 'capacity',            header: 'Capacity', sortable: true,
      render: (v) => `${v.capacity} seats` },
    { key: 'owner_name',          header: 'Owner',
      render: (v) => v.owner_name ?? (v.owner == null ? 'Unassigned' : `#${v.owner}`) },
    { key: 'is_active',           header: 'Status', filterable: true, filterOptions: [
      { value: 'true',  label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ], render: (v) => (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        v.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${v.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} aria-hidden />
        {v.is_active ? 'Active' : 'Inactive'}
      </span>
    ) },
  ];

  const rowActions = (v: Vehicle) => (
    <div className="inline-flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => openEdit(v)}>Edit</Button>
      <Button
        variant={v.is_active ? 'secondary' : 'primary'} size="sm"
        onClick={() => handleToggle(v)}
        loading={toggleActive.loading}
      >{v.is_active ? 'Deactivate' : 'Activate'}</Button>
    </div>
  );

  const stats = useMemo(() => {
    const active = vehicles.filter((v) => v.is_active).length;
    const inactive = vehicles.filter((v) => !v.is_active).length;
    const totalCapacity = vehicles.reduce((sum, v) => sum + (v.capacity || 0), 0);
    return { total: vehicles.length, active, inactive, totalCapacity };
  }, [vehicles]);

  return (
    <>
      <PageHeader
        title="Vehicles"
        description="Fleet vehicles and activation status."
        actions={
          isAdmin ? (
            <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
              New vehicle
            </Button>
          ) : undefined
        }
      />

      <StatStrip
        className="mb-6"
        stats={[
          { label: 'Total fleet', value: stats.total, icon: <Truck className="h-4 w-4" /> },
          { label: 'Active', value: stats.active, icon: <CheckCircle2 className="h-4 w-4" />, tone: 'income' },
          { label: 'Inactive', value: stats.inactive, icon: <Pause className="h-4 w-4" /> },
          { label: 'Total seats', value: stats.totalCapacity },
        ]}
      />

      <DataTable<Vehicle>
        columns={columns}
        data={vehicles}
        rowKey="id"
        loading={loading}
        emptyTitle="No vehicles yet"
        emptyDescription="Add your first vehicle to start scheduling trips."
        emptyAction={
          isAdmin ? (
            <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
              New vehicle
            </Button>
          ) : undefined
        }
        actions={isAdmin ? rowActions : undefined}
      />

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit vehicle' : 'New vehicle'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={save.loading}>Cancel</Button>
            <Button onClick={handleSubmit} loading={save.loading}>
              {editing ? 'Save changes' : 'Create vehicle'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Registration #" name="registration_number" required error={save.fieldErrors.registration_number}>
            <Input id="registration_number" required value={form.registration_number}
              onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
              className="font-mono uppercase"
            />
          </FormField>
          <FormField label="Capacity" name="capacity" required error={save.fieldErrors.capacity}>
            <Select
              id="capacity"
              value={form.capacity}
              onChange={(v) => setForm({ ...form, capacity: v })}
              placeholder="Select capacity"
              fullWidth
              options={(form.capacity && !CAPACITY_PRESETS.includes(form.capacity)
                ? [form.capacity, ...CAPACITY_PRESETS]
                : CAPACITY_PRESETS
              ).map((c) => ({ value: c, label: `${c} seats` }))}
            />
          </FormField>
          <FormField label="Type" name="vehicle_type" required error={save.fieldErrors.vehicle_type}>
            <Input id="vehicle_type" required value={form.vehicle_type}
              onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
            />
          </FormField>
          <FormField label="Owner" name="owner" hint="Partner — leave unassigned if company-owned" error={save.fieldErrors.owner}>
            <SearchableSelect
              id="owner"
              options={ownerOptions}
              value={form.owner}
              onChange={(v) => setForm({ ...form, owner: v })}
              placeholder="Select owner…"
              searchPlaceholder="Search owners by name…"
              aria-label="Owner"
            />
          </FormField>
        </form>
      </Modal>
    </>
  );
};

export default Vehicles;
